import { Check } from 'lucide-react';
import type { Plan } from './api';

export type PlanGate =
  | { kind: 'boolean'; key: string }
  | { kind: 'numeric'; key: string; used: number | null };

type PlanFeature = Plan['features'][number];

/** "No ceiling" is a *value type* -- a feature row carrying FeatureValueType.Unlimited with a null
 * intValue -- not a limit kind, which is only ever Hard/Soft/None. Testing limitKind for it (as this did)
 * is always false, so an unlimited allowance fell through to the numeric comparison below, failed it on
 * the null intValue, and the plan was filtered out of the upgrade picker as though it did not unlock the
 * feature at all. Exactly backwards for the most generous plan in the catalogue. */
const isUnlimited = (feature: PlanFeature) => feature.valueType === 'Unlimited';

export function planSupportsGate(plan: Plan, gate: PlanGate): boolean {
  const feature = plan.features.find((item) => item.featureKey === gate.key);
  if (!feature) return false;
  if (gate.kind === 'boolean') return feature.boolValue === true;
  if (isUnlimited(feature)) return true;
  return gate.used !== null && feature.intValue !== null && feature.intValue > gate.used;
}

const Gb = 1024 * 1024 * 1024;
const Tb = 1024 * Gb;

const booleanLabels: Record<string, string> = {
  'gallery.enabled': 'Public guest galleries',
  'camera.premium': 'Premium camera support',
  'analytics.advanced': 'Advanced analytics',
  'branding.custom': 'Custom branding',
  'whitelabel.enabled': 'White-label portal',
};

/** Singular/plural noun for the countable allowances, so "1 booth device" doesn't read as "1 booth devices". */
const countLabels: Record<string, [singular: string, plural: string]> = {
  'devices.max': ['booth device', 'booth devices'],
  'users.max': ['team member', 'team members'],
  'events.active.max': ['active event', 'active events'],
  'templates.max': ['template', 'templates'],
  'sessions.monthly': ['session / month', 'sessions / month'],
  'prints.monthly': ['print / month', 'prints / month'],
};

function formatStorage(bytes: number): string {
  return bytes >= Tb
    ? `${Number((bytes / Tb).toFixed(2))} TB storage`
    : `${Math.round(bytes / Gb)} GB storage`;
}

/** Long retentions read better in years -- "3-year photo retention" beats "1,095-day". Kept in days below
 * two years so 90 and 365 stay recognisably the numbers on the price list. */
function formatRetention(days: number): string {
  return days >= 730 && days % 365 === 0
    ? `${days / 365}-year photo retention`
    : `${days.toLocaleString()}-day photo retention`;
}

function formatFeature(feature: PlanFeature): string | null {
  const { featureKey: key } = feature;

  if (feature.valueType === 'Bool') {
    return feature.boolValue ? booleanLabels[key] ?? key : null;
  }

  const counts = countLabels[key];
  if (isUnlimited(feature)) return counts ? `Unlimited ${counts[1]}` : null;
  if (feature.intValue === null) return null;

  if (key === 'storage.bytes') return formatStorage(feature.intValue);
  if (key === 'retention.days.max') return formatRetention(feature.intValue);
  if (key === 'device.offline.grace.hours') return `${feature.intValue}-hour offline grace`;
  if (!counts) return null;

  return `${feature.intValue.toLocaleString()} ${feature.intValue === 1 ? counts[0] : counts[1]}`;
}

/**
 * What each tier is sold on, in the order it should be read.
 *
 * Previously every feature the plan carried was listed in whatever order the API returned them, which
 * buried the three or four lines that actually decide an upgrade among ten that do not. Which lines earn
 * a place on a card, and their order, is an editorial judgement that plan data cannot express -- but the
 * *values* are still read from the plan, so an admin changing a limit updates the card rather than leaving
 * it quietly lying about what was bought.
 *
 * Tiers above Starter open with "Everything in <previous>" and then list only what improves, so the
 * upgrade decision is the difference rather than a diff the reader has to perform themselves.
 */
const planCards: Record<string, { inherits?: string; badge?: string; keys: string[] }> = {
  trial: {
    keys: [
      'devices.max', 'events.active.max', 'sessions.monthly', 'prints.monthly',
      'storage.bytes', 'retention.days.max', 'users.max',
      'gallery.enabled', 'camera.premium', 'branding.custom',
    ],
  },
  starter: {
    keys: [
      'devices.max', 'events.active.max', 'sessions.monthly', 'prints.monthly',
      'storage.bytes', 'retention.days.max', 'users.max',
    ],
  },
  pro: {
    inherits: 'Starter',
    badge: 'Most Popular',
    // Volume lines are ordered as Starter orders them, so the numbers a reader is comparing sit on the
    // same row of both cards instead of having to be hunted for.
    keys: [
      'devices.max', 'events.active.max', 'sessions.monthly', 'prints.monthly',
      'gallery.enabled', 'branding.custom', 'camera.premium',
      'storage.bytes', 'retention.days.max', 'users.max',
    ],
  },
  studio: {
    inherits: 'Pro',
    keys: [
      'devices.max', 'events.active.max', 'sessions.monthly', 'prints.monthly',
      'whitelabel.enabled', 'analytics.advanced',
      'storage.bytes', 'retention.days.max', 'users.max',
    ],
  },
};

function planHighlights(plan: Plan): string[] {
  const byKey = new Map(plan.features.map((feature) => [feature.featureKey, feature]));
  const card = planCards[plan.code];

  // A plan the catalogue doesn't know about -- one added through the admin API -- still gets a usable
  // card rather than an empty one, by falling back to listing whatever it carries.
  const keys = card?.keys ?? plan.features.map((feature) => feature.featureKey);

  const lines = keys.flatMap((key) => {
    const feature = byKey.get(key);
    if (!feature) return [];
    const line = formatFeature(feature);
    return line ? [line] : [];
  });

  return card?.inherits ? [`Everything in ${card.inherits}`, ...lines] : lines;
}

/** The headline saving from paying yearly, taken across the plans that price both intervals. Quoted from
 * the real prices rather than a hardcoded figure, which had drifted: the copy claimed 10% while the
 * catalogue was priced at about 17%. */
function yearlySavingPercent(plans: Plan[]): number | null {
  const savings = plans.flatMap((plan) =>
    plan.priceMonthly && plan.priceYearly
      ? [1 - plan.priceYearly / (plan.priceMonthly * 12)]
      : []);
  if (savings.length === 0) return null;
  const best = Math.round(Math.max(...savings) * 100);
  return best > 0 ? best : null;
}

export function PlanPicker({
  plans,
  currentPlanCode,
  interval,
  onIntervalChange,
  onChoose,
  pending = false,
  gate,
}: {
  plans: Plan[];
  currentPlanCode?: string;
  interval: 'monthly' | 'yearly';
  onIntervalChange: (interval: 'monthly' | 'yearly') => void;
  onChoose: (plan: Plan, interval: 'monthly' | 'yearly') => void;
  pending?: boolean;
  gate?: PlanGate;
}) {
  const availablePlans = gate ? plans.filter((plan) => planSupportsGate(plan, gate)) : plans;
  const recommendedCode = gate ? availablePlans.find((plan) => plan.code !== currentPlanCode)?.code : undefined;
  const saving = yearlySavingPercent(plans);

  return <div className="bv-form-stack">
    <div className="bv-billing-plans__head">
      <div className="bv-segmented" role="group" aria-label="Billing interval">
        <button className="bv-segmented__option" type="button" aria-pressed={interval === 'monthly'} onClick={() => onIntervalChange('monthly')}>Monthly</button>
        <button className="bv-segmented__option" type="button" aria-pressed={interval === 'yearly'} onClick={() => onIntervalChange('yearly')}>
          Yearly{saving !== null && <span className="bv-segmented__hint">Save {saving}%</span>}
        </button>
      </div>
    </div>
    {availablePlans.length === 0 ? <p role="status">No available plan currently unlocks this feature. Please contact support.</p> : <div className="bv-billing-plan-grid">
      {availablePlans.map((plan) => {
        const isCurrent = currentPlanCode === plan.code;
        // A contextual recommendation is more use than a standing one, so a gate-driven "Recommended"
        // wins over the catalogue's "Most Popular" rather than the card carrying both.
        const isRecommended = recommendedCode === plan.code;
        const badge = isRecommended ? 'Recommended' : planCards[plan.code]?.badge;
        const highlights = planHighlights(plan);
        // A plan can genuinely have no price configured for one interval (nothing requires both to be
        // set, and the free trial has neither). Rendering the raw null left a bare "AUD/yr" with no
        // number, so say so plainly and don't offer a button that can't work.
        const price = interval === 'monthly' ? plan.priceMonthly : plan.priceYearly;
        const effectiveMonthly = interval === 'yearly' && price ? price / 12 : null;
        return <div className={`bv-billing-plan-card${isRecommended ? ' bv-billing-plan-card--recommended' : ''}${badge && !isRecommended ? ' bv-billing-plan-card--popular' : ''}${isCurrent ? ' bv-billing-plan-card--current' : ''}`} key={plan.id}>
          {badge && <span className="bv-billing-plan-card__badge">{badge}</span>}
          <h3 className="bv-billing-plan-card__name">{plan.name}</h3>
          {plan.description ? <p className="bv-muted">{plan.description}</p> : null}
          <p className="bv-billing-plan-card__price">
            {price === null
              ? <span className="bv-billing-plan-card__price-unset">{plan.priceMonthly === null && plan.priceYearly === null ? 'Free' : 'Contact us for pricing'}</span>
              : <>${price}<span className="bv-supporting-text"> {plan.currency}/{interval === 'monthly' ? 'mo' : 'yr'}</span></>}
          </p>
          {effectiveMonthly !== null && <p className="bv-supporting-text bv-billing-plan-card__effective">
            ≈ ${effectiveMonthly.toFixed(2)} {plan.currency}/mo billed yearly
          </p>}
          {highlights.length > 0 && <ul className="bv-billing-plan-card__features">
            {highlights.map((line) => <li key={line}><Check aria-hidden="true" size={15} />{line}</li>)}
          </ul>}
          <button
            className={`bv-button${isCurrent ? ' bv-button--quiet' : ''}`}
            type="button"
            disabled={pending || isCurrent || price === null}
            onClick={() => onChoose(plan, interval)}
          >
            {isCurrent ? 'Current plan' : price === null ? 'Unavailable' : pending ? 'Redirecting…' : 'Choose plan'}
          </button>
        </div>;
      })}
    </div>}
  </div>;
}
